import { useState, useEffect } from 'react';
import { Link2, Sparkles, Trash2, Plus } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Association {
  id: string;
  product_id: string;
  associated_product_id: string;
  association_type: string;
  score: number;
  product_name?: string;
  associated_name?: string;
}

export default function UpsellAssociations() {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for manual add
  const [productId, setProductId] = useState('');
  const [associatedProductId, setAssociatedProductId] = useState('');
  const [associationType, setAssociationType] = useState('cross_sell');

  useEffect(() => {
    fetchAssociations();
  }, []);

  async function fetchAssociations() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ associations: Association[] }>('/admin/associations');
      setAssociations(response.associations || []);
    } catch (err) {
      setError('Failed to load associations. Please try again.');
      console.error('Error fetching associations:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAutoGenerate() {
    setSuccessMessage(null);

    try {
      setIsGenerating(true);
      setError(null);
      const response = await api.post<{ count: number }>('/admin/associations/auto-generate', {});
      setSuccessMessage(`Successfully generated ${response.count} product associations`);
      setTimeout(() => setSuccessMessage(null), 4000);
      await fetchAssociations();
    } catch (err) {
      setError('Failed to auto-generate associations. Please try again.');
      console.error('Error auto-generating associations:', err);
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleAddAssociation(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    if (!productId || !associatedProductId) {
      setError('Please enter both product IDs');
      return;
    }

    try {
      setIsAdding(true);
      setError(null);
      await api.post('/admin/associations', {
        product_id: productId.trim(),
        associated_product_id: associatedProductId.trim(),
        association_type: associationType,
      });
      setSuccessMessage('Association added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setProductId('');
      setAssociatedProductId('');
      await fetchAssociations();
    } catch (err) {
      setError('Failed to add association. Please try again.');
      console.error('Error adding association:', err);
    } finally {
      setIsAdding(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this association?')) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/admin/associations/${id}`);
      setSuccessMessage('Association deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAssociations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError('Failed to delete association. Please try again.');
      console.error('Error deleting association:', err);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading product associations...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Link2 className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Product Associations</h1>
        </div>
        <p className="text-gray-600">Manage related product recommendations and cross-sells</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Auto-Generate Associations" padding="lg">
          <p className="text-sm text-stone-600 mb-4">
            Automatically generate product associations based on purchase history, categories, and product attributes.
          </p>
          <Button
            type="button"
            variant="primary"
            size="md"
            isLoading={isGenerating}
            onClick={handleAutoGenerate}
          >
            <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />
            Auto-Generate
          </Button>
        </Card>

        <Card title="Add Manual Association" padding="lg">
          <form onSubmit={handleAddAssociation} className="space-y-4">
            <Input
              label="Product ID"
              type="text"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              placeholder="UUID of the source product"
            />
            <Input
              label="Associated Product ID"
              type="text"
              value={associatedProductId}
              onChange={(e) => setAssociatedProductId(e.target.value)}
              placeholder="UUID of the associated product"
            />
            <div>
              <label htmlFor="association_type" className="block text-sm font-medium text-stone-700 mb-1">
                Association Type
              </label>
              <select
                id="association_type"
                value={associationType}
                onChange={(e) => setAssociationType(e.target.value)}
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="cross_sell">Cross-sell</option>
                <option value="upsell">Upsell</option>
                <option value="complementary">Complementary</option>
                <option value="frequently_bought_together">Frequently Bought Together</option>
              </select>
            </div>
            <Button type="submit" variant="primary" size="md" isLoading={isAdding}>
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              Add Association
            </Button>
          </form>
        </Card>
      </div>

      <Card title={`All Associations (${associations.length})`} padding="lg">
        {associations.length === 0 ? (
          <p className="text-stone-500 text-center py-8">
            No associations yet. Use auto-generate or add manually to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Product</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Associated Product</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Type</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Score</th>
                  <th className="text-right py-3 px-2 font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {associations.map(assoc => (
                  <tr key={assoc.id} className="border-b border-stone-100">
                    <td className="py-3 px-2 text-stone-900">
                      <div>{assoc.product_name || 'Unknown'}</div>
                      <div className="text-xs text-stone-400 font-mono">{assoc.product_id.slice(0, 8)}...</div>
                    </td>
                    <td className="py-3 px-2 text-stone-900">
                      <div>{assoc.associated_name || 'Unknown'}</div>
                      <div className="text-xs text-stone-400 font-mono">{assoc.associated_product_id.slice(0, 8)}...</div>
                    </td>
                    <td className="py-3 px-2 text-stone-600">{assoc.association_type.replace(/_/g, ' ')}</td>
                    <td className="py-3 px-2 text-stone-600">{assoc.score?.toFixed(2) || 'N/A'}</td>
                    <td className="py-3 px-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(assoc.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
